import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import { ClusterOutlined, ReloadOutlined, ShopOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FONT_SIZE, FONT_WEIGHT, SPACING, RADIUS, LINE_HEIGHT, COMPACT_LINK_STYLE, LAYOUT, TABLE_COL, type ThemeTokens,
} from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsPhone, useIsWide } from '../../hooks/UseIsCompact';
import { STORAGE_KEY, useStorage } from '../../contexts/StorageContext';
import { applyCurrentToHub } from '../../api/api';
import { useNavigation } from '../../contexts/NavigationContext';
import { writePriceLookupFocus } from '../../utils/priceLookupFocus';
import {
  ApplyCurrentApplied, HubChangesResponse, HubPendingRequest, isPendingApplied,
} from '../../types/hubChanges';
import { NumberCell, formatNumber, numberHeader } from '../common/NumberCell';
import { appMessage as message } from '../../utils/appMessage';
import { AppButton } from '../common/AppButton';
import { AppModal } from '../common/AppModal';
import { AppLoadError } from '../common/AppLoadError';
import { TableEmptyState, TableSkeleton } from '../common/TableHelpers';
import { cardPlaceholder, fallbackToBroken } from '../placeholders';
import { TablePager, VIRTUAL_FROM_PAGE_SIZE, columnsScrollX, useTablePaging, useVirtualScrollY } from '../common/TablePager';
import { BulkActionBar, groupExcluded } from '../common/BulkActionBar';
import { BulkPreviewModal } from '../common/BulkPreviewModal';
import { READ_ONLY_REASON } from '../../constants/readOnly';
import {
  HUB_STATUS_LABEL, HubRow, HubStatusTab, RowAdvice, applyCurrentBlockReason, applyCurrentExcludeLabel, hubCheckingTooltip,
  hubManualPrice, hubRowAdvice, hubRowBlockReason, hubRowStatus, hubStorePrice,
} from './hubRows';

/**
 * 다른 화면이 매장 가격 요청을 특정 보기로 열어 달라고 남기는 한 번짜리 힌트(sessionStorage) — 판매가 결정 저장 결과의
 * "매장 가격 요청에서 보기" 가 'pending'(PriceHub 확인 중)을 쓴다(감사 card-controltower#505). 쓰는 쪽(DecidedPricePage)과 키가 같아야 한다 —
 * 두 PR(#74 · #75)을 따로 넣을 수 있게 상수를 각자 둔다.
 */
export const STORE_PRICE_OPEN_TAB_KEY = 'storePrice:openTab';

export function readOpenTabHint(): HubStatusTab {
  try {
    const v = sessionStorage.getItem(STORE_PRICE_OPEN_TAB_KEY);
    return v === 'pending' || v === 'blocked' ? v : 'available';
  } catch {
    return 'available';
  }
}

/**
 * PriceHub 대시보드 주소(빌드 환경값 REACT_APP_PRICEHUB_URL, 예: http://<PriceHub 서버>) — 있으면 'PriceHub 확인 중' 칩이 그 매장의
 * PriceHub '확인할 요청' 탭(/store-price-check/<매장>/?tab=notices)으로 가는 링크다. 없으면 칩은 글(툴팁)만.
 */
export const PRICEHUB_URL = (process.env.REACT_APP_PRICEHUB_URL ?? '').replace(/\/+$/, '');
export function pricehubRequestsUrl(store: string | null | undefined, base: string = PRICEHUB_URL): string | null {
  if (!base || !store) return null;
  return `${base}/store-price-check/${encodeURIComponent(store.toLowerCase())}/?tab=notices`;
}

/**
 * 'PriceHub 확인 중' 칩 하나(사장 확정 2026-11-04 15:45 — 예전 배지 다섯 종(알림 종류 · 확정 대기 · 확정 대기(CT 요청) · 규칙 제안 ·
 * 매장 직접 변경)을 이것 하나로). 툴팁은 후보 가격 · 시각 · 요청자, 누르면 PriceHub 확인할 요청 탭.
 */
export const HUB_CHECKING_LABEL = '⏳ PriceHub 확인 중';
const CheckingChip: React.FC<{ r: HubRow; store: string | null | undefined; t: ThemeTokens }> = ({ r, store, t }) => {
  const tip = hubCheckingTooltip(r);
  const href = pricehubRequestsUrl(store);
  // 누르는 곳은 44px(코덱 리뷰 #871 — 칩 글씨 크기 그대로라 18px 남짓이었다). 보이는 칩은 안쪽 span, 바깥 a · span 이 누르는 곳.
  const hit: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', minHeight: 44, minWidth: 44, textDecoration: 'none', boxSizing: 'border-box',
  };
  const chip = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
      color: t.warning, background: t.tintWarning, border: `1px solid ${t.lineWarning}`, borderRadius: RADIUS.sm,
      padding: `0 ${SPACING.xxs}px`, whiteSpace: 'nowrap', lineHeight: LINE_HEIGHT.snug,
    }}>
      {HUB_CHECKING_LABEL}
    </span>
  );
  return (
    <Tooltip title={href ? `${tip} — 누르면 PriceHub 확인할 요청 탭` : tip}>
      {href ? (
        <a data-testid="hub-checking" href={href} target="_blank" rel="noopener noreferrer" style={hit}
          aria-label={`${tip} (PriceHub 확인할 요청 탭 열기)`}>
          {chip}
        </a>
      ) : (
        <span data-testid="hub-checking" tabIndex={0} style={hit} aria-label={tip}>
          {chip}
        </span>
      )}
    </Tooltip>
  );
};
/** 응답을 받지 못한 요청의 카드 — 목록을 다시 받을 때까지 다시 보내지 않는다(감사 #55). */
const UNKNOWN_RESULT_REASON = '보낸 요청의 응답을 받지 못했습니다 — PriceHub 가 응답한 목록을 다시 받은 뒤 결과를 확인하고 보내 주세요(PriceHub 장애 중 받은 목록으로는 풀리지 않습니다).';

/** 요청을 보낸 직후(목록을 다시 받기 전) 줄에 덮는 서버와 같은 사유. */
const LOCAL_PENDING_REASON = 'PriceHub 확인 중 — PriceHub 관리자가 확인한 뒤 다시 요청할 수 있습니다.';
/** 설명 띠의 한 줄 요약(설계 2026-11-04 §3) — 전체 설명은 '설명 보기' 로 펼친다. */
export const hubHelpSummary = (): string =>
  '네이버 판매가와 PriceHub 가격이 다른 카드입니다. 줄마다 어느 쪽에 맞출지 안내합니다 — 반영: 네이버를 PriceHub 에 · 요청: PriceHub 를 네이버에.';
/** 확인 중 · 대상 아님 보기의 글 칸 폭. */
const INFO_WIDTH = 280;

/**
 * 표(넓은 화면) 칸은 한 줄로(감사 card-controltower#326 — 두 줄 칸 · 위아래 버튼으로 한 줄이 약 73px 이라 1893×905 에서 목록이
 * 4줄만 보였다). 보조 글(상품코드)은 작은 글씨로 같은 줄에 두고, 넘치면 말줄임 — 전체는 title 툴팁.
 */
export const ONE_LINE: React.CSSProperties = {
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
};

/** 가격 칸 — 값은 둘만(네이버 판매가 · PriceHub 가격). 오른쪽 정렬 · 고정폭 숫자, 기준 쪽엔 '기준' 꼬리표. */
const PRICE_CELL: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0, minWidth: 0,
  textAlign: 'right', fontVariantNumeric: 'tabular-nums', lineHeight: LINE_HEIGHT.snug,
};
const NOWRAP: React.CSSProperties = { whiteSpace: 'nowrap' };
const won = (n: number) => n.toLocaleString('ko-KR');

/** '기준' 꼬리표(설계 §2-3) — 어느 값이 기준인지 칸 위치로도 보이게. */
const BaseTag: React.FC<{ t: ThemeTokens }> = ({ t }) => (
  <span data-testid="hub-base-tag" style={{
    fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.medium, color: t.textSecondary, border: `1px solid ${t.border}`,
    borderRadius: RADIUS.sm, padding: `0 ${SPACING.xxs}px`, marginLeft: SPACING.xxs, whiteSpace: 'nowrap', lineHeight: LINE_HEIGHT.snug,
  }}>
    기준
  </span>
);

/**
 * 차이 칸 — 방향을 말로: "네이버가 21,000 높음(70%)" / "네이버가 1,600 낮음(80%)". 부호 · 화살표(▲/▼)는 쓰지 않는다 — 화살표는 '지금 할 일'
 * 칸의 바뀌는 값에만(시우 검수 #99 M1, 팀장 결정 (a): 같은 금액이 두 칸에 반대 부호로 보여 헷갈렸다). %는 네이버 판매가 대비 크기, 작은 글씨.
 * 색은 보조(hubRise · hubDrop 토큰), 뜻은 글자로.
 */
const DiffCell: React.FC<{ naver: number | null; hub: number | null; t: ThemeTokens }> = ({ naver, hub, t }) => {
  if (naver == null || hub == null || naver <= 0 || naver === hub) return <span style={{ color: t.textEmpty }}>-</span>;
  const naverHigher = naver > hub;
  const c = t.tag(naverHigher ? 'hubDrop' : 'hubRise');
  const pct = Math.round((Math.abs(hub - naver) / naver) * 100);
  return (
    <span data-testid="hub-diff" style={{
      fontVariantNumeric: 'tabular-nums', color: c.text, fontWeight: FONT_WEIGHT.medium, whiteSpace: 'normal', wordBreak: 'keep-all',
    }}>
      네이버가 <span style={NOWRAP}>{won(Math.abs(hub - naver))}</span> {naverHigher ? '높음' : '낮음'}
      <span style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.regular }}>({pct}%)</span>
    </span>
  );
};
/**
 * 지금 할 일 칸(설계 §2-3) — 첫 줄 = 기준값(굵게 + '기준' 꼬리표; 반영이면 PriceHub 색, 요청이면 네이버 색), 둘째 줄 = 바뀌는 값
 * `옛 값(흐리게) → 새 값(굵게)` + ▲/▼ 금액 + 꼬리 글. 확인 필요 · 가격 받기 먼저 · 확인 중 · 대상 아님은 한 줄 글. 전체 글은 title.
 */
const AdviceCell: React.FC<{ r: HubRow; a: RowAdvice | null; store: string | null | undefined; t: ThemeTokens }> = ({ r, a, store, t }) => {
  if (!a) return null;
  const box: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0, fontSize: FONT_SIZE.xs, color: t.textSecondary,
    lineHeight: LINE_HEIGHT.snug, fontVariantNumeric: 'tabular-nums', whiteSpace: 'normal', wordBreak: 'keep-all',
  };
  if (a.kind === 'checking') {
    return (
      <div data-testid="hub-advice" data-kind={a.kind} title={a.text} style={{ ...box, flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xxs, alignItems: 'center' }}>
        <CheckingChip r={r} store={store} t={t} />
        <span>{a.tail}</span>
      </div>
    );
  }
  if ((a.kind === 'apply' || a.kind === 'request') && a.from != null && a.to != null) {
    // 기준 값 = 바뀌는 값이 맞춰 갈 새 값(반영: PriceHub 가격, 요청: 네이버 판매가).
    const baseValue = a.to;
    const baseName = a.base === 'hub' ? 'PriceHub' : '네이버';
    const movingName = a.base === 'hub' ? '네이버' : 'PriceHub';
    const strong: React.CSSProperties = { ...NOWRAP, fontWeight: FONT_WEIGHT.bold, color: a.base === 'hub' ? t.accent : t.textPrimary };
    const showDelta = a.text.includes('▲') || a.text.includes('▼');
    return (
      <div data-testid="hub-advice" data-kind={a.kind} title={a.text} style={box}>
        <span style={NOWRAP}>
          <span style={strong}>{baseName} {won(baseValue)}</span><BaseTag t={t} />
        </span>
        <span>
          <span style={NOWRAP}>{movingName} <span style={{ color: t.textMuted }}>{won(a.from)}</span> → <b style={{ color: t.textPrimary }}>{won(a.to)}</b></span>
          {showDelta && <span style={{ ...NOWRAP, marginLeft: 4 }}>{a.to > a.from ? '▲' : '▼'}{won(Math.abs(a.to - a.from))}</span>}
          {' — '}{a.tail}
        </span>
        <ManualPriceNote r={r} t={t} />
      </div>
    );
  }
  return (
    <div data-testid="hub-advice" data-kind={a.kind} title={a.text} style={box}>
      <span style={{ color: a.kind === 'excluded' ? t.textSecondary : t.textPrimary }}>{a.text}</span>
      {(a.kind === 'check' || a.kind === 'fetchFirst') && <ManualPriceNote r={r} t={t} />}
    </div>
  );
};

/**
 * '수동 수정가 N원 있음'(코덱 리뷰 #868) — CT 수정가가 PriceHub 가격과 다르면(② 에서 적용가를 고침 · 수동가). ③ 반영은 수정가를 보내므로
 * 반영 전에 알 수 있게 안내 줄 아래에 둔다. 추천은 PriceHub 가격 기준 그대로.
 */
const ManualPriceNote: React.FC<{ r: HubRow; t: ThemeTokens }> = ({ r, t }) => {
  const m = hubManualPrice(r);
  if (m == null) return null;
  return (
    <span data-testid="hub-manual-price" style={{ color: t.warning, fontWeight: FONT_WEIGHT.medium }}>
      수동 수정가 {won(m)}원 있음 — 반영하면 이 값이 나갑니다
    </span>
  );
};

/**
 * apply-current 결과 안내(감사 card-controltower#71) — 요청 대기열 뒤로는 "요청 N건 · PriceHub 확인 중", 바로 바뀐 것(예전 응답 ·
 * PriceHub 가 이미 같은 값)은 따로 센다.
 */
export function appliedSummary(applied: ApplyCurrentApplied[]): string {
  const pendingN = applied.filter(isPendingApplied).length;
  const settledN = applied.length - pendingN;
  const parts: string[] = [];
  if (pendingN > 0) {
    parts.push(`요청 ${pendingN.toLocaleString()}건 · PriceHub 확인 중 — PriceHub 관리자가 확인하면 PriceHub 가격이 바뀝니다.`);
  }
  if (settledN > 0) parts.push(`${settledN.toLocaleString()}건의 PriceHub 가격을 네이버 판매가로 바꿨습니다.`);
  return parts.join(' ');
}

/** 'PriceHub 가격' 열의 값 — PriceHub 가 이 매장에 주는 기준값(hubStorePrice). */
const priceTarget = hubStorePrice;

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;
const serverMessage = (e: unknown): string | undefined =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
/**
 * 409 가 PriceHub 의 것인가(값이 그 사이 바뀜 · 이미 처리됨 — 응답에 pricehubStatus 가 있다). 없으면 CT 의 잡 락 409(가격 조회 ·
 * 재고 동기화 · 다른 확정이 도는 중 — 백엔드 PR #45, 감사 card-controltower#80)라 서버 문구를 그대로 보여 준다.
 */
const fromPricehub = (e: unknown): boolean =>
  (e as { response?: { data?: { pricehubStatus?: unknown } } })?.response?.data?.pricehubStatus != null;
/**
 * 백엔드가 PriceHub 응답 타임아웃을 502 + resultUnknown=true 로 준다(ApiExceptionHandler, 감사 card-controltower#83 ②) — 요청은
 * 나갔는데 PriceHub 가 받았는지 모른다. '요청 실패' 가 아니라 '결과 불명' 으로 읽는다(감사 card-controltower#312).
 */
const resultUnknownOf = (e: unknown): boolean =>
  (e as { response?: { data?: { resultUnknown?: unknown } } })?.response?.data?.resultUnknown === true;
/** 응답 본문의 카드별 실패 사유가 '결과 불명'(PriceHub 응답 타임아웃 — 백엔드 RESULT_UNKNOWN_MESSAGE · 예전 문구)인가. */
const UNCLEAR_REASON = /결과 불명|timeout|timed out|시간 초과|응답(이)? 없|응답하지 않/i;

export interface HubChangesTableProps {
  data:      HubChangesResponse | null;
  /** 카드 기준으로 합친 줄(mergeHubItems). */
  rows:      HubRow[];
  loading:   boolean;
  error:     Error | null;
  onReload:  () => void;
  /** 체크된 카드 id — "매장가 요청" 화면이 따로 가진 선택(사용자가 이 화면에서 고른 것만, 감사 card-controltower#330). */
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
  /** 필터(종류 · 어제 이후만)를 통과해 보이는 체크 가능 카드 id — 필터가 없으면 null. */
  onVisibleIdsChange?: (ids: number[] | null) => void;
  /** 네이버 가격 → <매장> 가격 요청 뒤 — 로컬 카드 값 맞추기 · 선택 정리. */
  onAppliedCurrent?: (applied: ApplyCurrentApplied[]) => void;
  /**
   * 가상 스크롤(500줄 이상 페이지)일 때만 쓰는 표 높이 — 그 밖에는 표가 페이지와 함께 스크롤되고 머리줄 · 선택 줄이 화면 위에
   * 붙는다(감사 card-controltower#326 — 표 안 스크롤 높이를 고정 계산해 1893×905 에서 목록 칸이 277px 이었다).
   */
  scrollY?: string | number;
  /** 지금 data 를 낸 조회가 시작된 시각(ms) — 응답 불명 표시를 지울지 가린다(감사 #281). 없으면 목록이 바뀔 때 모두 지운다. */
  dataStartedAt?: number | null;
  /**
   * 상태 탭을 화면이 쥘 때(저장 필터 칩 '확정 대기' · '대상 아님' 과 같은 값 — 가격 목록 개편 PR 3, 감사 card-controltower#697).
   * 주면 탭은 이 값을 따르고 탭을 누르면 `onStatusTabChange` 만 부른다. 없으면 표가 스스로 쥔다(예전 그대로).
   */
  statusTab?: HubStatusTab;
  onStatusTabChange?: (tab: HubStatusTab) => void;
}

interface ApplyFailure { cardId: number; name: string; reason: string }

/** 나눠 보내다 멈춘 이유 — 매장 전환 · 로그아웃 · 다른 계정으로 다시 로그인 · 화면 이탈(언마운트, 탭 전환 포함). */
type ApplyStopReason = 'store' | 'logout' | 'account' | 'unmount';

/**
 * 지금 요청이 나갈 세션 — http 가 요청마다 읽는 값(superStore → X-Store-Code)과 쿠키 주인을 가리키는 값(role · store ·
 * username). 다른 탭에서 다른 매장 ADMIN 으로 다시 로그인하면 superStore 는 그대로 null 이라 이것까지 봐야 한다
 * (Opus 리뷰 PR #43 중간 2).
 */
const sessionOf = () => [
  STORAGE_KEY.role, STORAGE_KEY.store, STORAGE_KEY.superStore, STORAGE_KEY.username,
].map((k) => localStorage.getItem(k) ?? '').join('|');
interface ApplyStop { reason: ApplyStopReason; unsent: number; unknown: number }

/** apply-current 한 번에 보내는 최대 장수(백엔드 한도 — 넘으면 400, 2026-10-21 PR #44 최종). */
export const APPLY_CURRENT_CHUNK = 200;

/** 두 방향 버튼의 확인 대화상자 — 표 전체가 하나를 같이 쓴다(줄마다 팝오버를 두면 줄 수만큼 무거워진다). */
interface PendingAction { title: string; description: string; okText: string; run: () => void }

/**
 * 표 위 설명(펼친 때) — 용어 사전(2026-11-04 개정 — 작업자 기준 모델)의 말만.
 */
export const hubHelpText = (): string =>
  '네이버 판매가 = 네이버 스마트스토어에 지금 실제로 걸린 값 · PriceHub 가격 = PriceHub 가 이 매장에 주는 기준값. ' +
  '두 값이 같은 카드는 할 일이 없어 보이지 않습니다. 반영은 네이버 판매가를 PriceHub 가격으로 바꾸는 것으로 가격 조회의 ③ 에서 하고, ' +
  '요청은 PriceHub 가격을 네이버 판매가로 바꿔 달라고 PriceHub 관리자에게 보내는 것입니다(확정 · 무시는 PriceHub 에서 사람이 합니다). ' +
  "PriceHub 관리자가 아직 확인하지 않은 카드는 'PriceHub 확인 중' 에 보이고, 그동안은 반영 · 요청을 쉽니다.";
/** 일괄 요청 미리보기의 한 줄 설명 — 무엇을 하는지(요청만 · 네이버 판매가는 그대로). */
export const bulkRequestText = (rows: HubRow[]): string =>
  `선택 ${rows.length.toLocaleString()}건 — PriceHub 가격을 각 카드의 네이버 판매가로 바꿔 달라고 PriceHub 관리자에게 보냅니다. `
  + '확인되기 전까지 이 카드는 반영하지 않습니다. 네이버 판매가는 그대로입니다.';
/**
 * 일괄 요청 미리보기의 실행 대상 — 미리보기에 보인 줄(스냅샷)을 지금 목록과 대조한다(코덱 리뷰 #715). 네이버 판매가(요청 값) ·
 * PriceHub 가격(지금 값)이 미리보기 뒤 바뀐 카드는 changed, 목록에서 빠졌거나 지금 보낼 수 없게 된(확인 중 · 응답 불명 잠금 등)
 * 카드는 gone 으로 세고 둘 다 보내지 않는다 — 화면에서 확인한 금액과 실제 요청 금액이 달라지지 않게.
 */
export function previewSendable(snapshot: HubRow[], nowRows: HubRow[]): { ids: number[]; changed: number; gone: number } {
  const now = new Map(nowRows.filter((r) => r.cardId != null).map((r) => [r.cardId as number, r]));
  const ids: number[] = [];
  let changed = 0;
  let gone = 0;
  snapshot.forEach((s) => {
    const r = s.cardId != null ? now.get(s.cardId) : undefined;
    if (!r || hubRowStatus(r) !== 'available' || hubRowBlockReason(r)) { gone += 1; return; }
    if (r.currentPrice !== s.currentPrice || priceTarget(r) !== priceTarget(s)) { changed += 1; return; }
    ids.push(r.cardId as number);
  });
  return { ids, changed, gone };
}
/** 처리 열 폭 — '반영' · '요청' 두 단어 버튼(설계 2026-11-04 §2-3 — 버튼 글은 한 단어, #560 폭 규칙). */
const ACTION_WIDTH = 116;

/**
 * 매장 가격 요청 표(설계 2026-11-04 '작업자 기준 모델' — 사장 확정 15:40 · 15:45).
 *
 * 작업자가 보는 값은 둘(네이버 판매가 · PriceHub 가격), 할 수 있는 일도 둘(반영 · 요청). 두 값이 다른 카드만 뜨고(같으면 어떤 알림이
 * 걸려 있어도 안 뜬다), 줄마다 "기준 → 바뀌는 값" 한 줄로 어느 쪽에 맞출지 안내한다(rowAdvice). 보기는 칩 셋 — 할 일(기본) ·
 * PriceHub 확인 중 · 대상 아님. 이 화면의 쓰기는 요청(apply-current)뿐이고, 반영은 가격 조회 ③ 으로 옮겨 준다(③ 은 ② 하락 검토 ·
 * 반영 확인 창 · 상한 게이트를 거친다 — 세 화면 재설계 F3 안 A). 확정 · 무시는 PriceHub 에서만(사용자 결정 2026-10-22, 감사 #100).
 */
export const HubChangesTable: React.FC<HubChangesTableProps> = ({
  data, rows, loading, error, onReload, selectedIds, onSelectedIdsChange,
  onAppliedCurrent,
  onVisibleIdsChange, scrollY, dataStartedAt,
  statusTab: statusTabProp, onStatusTabChange,
}) => {
  const t = useThemeTokens();
  // 휴대폰 폭(≤640px)은 표 대신 카드형 한 열 + 누르는 곳 44px(2026-10-21 코덱 화면 점검).
  const phone = useIsPhone();
  const wide = useIsWide();
  const [helpOpen, setHelpOpen] = useState(false);
  // 일괄 작업 바(화면 위에 붙는다)의 높이 — 표 머리줄이 그 바로 아래 붙도록 CSS 변수 --hub-bar-h 로 넘긴다(줄바꿈 · 진행 글로
  // 바뀐다). 바는 1건 이상 고를 때만 보이고(결정 D5) 숨으면 0 — BulkActionBar 가 잰다.
  const [barH, setBarH] = useState(0);
  // 일괄 요청 전 미리보기(결정 D6) — 열린 동안의 대상(열 때 고정) · 제외 사유.
  const [bulkPreview, setBulkPreview] = useState<{ rows: HubRow[]; excluded: { label: string; count: number }[]; store: string | null } | null>(null);
  const tap: React.CSSProperties | undefined = phone ? { height: 44 } : undefined;
  const linkStyle: React.CSSProperties = phone ? { ...COMPACT_LINK_STYLE, minHeight: 44 } : COMPACT_LINK_STYLE;
  const { isAdmin, superStore, isLoggedIn, store, username } = useStorage();
  const { go } = useNavigation();
  // [반영] · [가격 받기] — 가격 조회 화면을 그 카드에 맞춰 연다(그 카드의 탭 · 페이지로 옮겨 줄을 표시 — 코덱 리뷰 #869).
  const openInPriceLookup = (r: HubRow) => {
    if (r.cardId != null) writePriceLookupFocus({ cardId: r.cardId, code: r.sellerProductCode, name: r.productName ?? r.sellerProductCode });
    go('daily');
  };
  // 보기 칩(설계 §3) — 기본 '할 일'. 체크 · 요청은 할 일에서만, 확인 중 · 대상 아님은 글로 보인다. 판매가 결정의
  // "매장 가격 요청에서 보기" 로 왔으면 그 힌트의 보기(확인 중)로 연다(감사 #505). 힌트는 읽기만 하고 지우기는 effect 에서
  // (StrictMode 가 초기화 함수를 두 번 불러도 같은 값).
  const [ownStatusTab, setOwnStatusTab] = useState<HubStatusTab>(readOpenTabHint);
  const statusTab = statusTabProp ?? ownStatusTab;
  useEffect(() => {
    try { sessionStorage.removeItem(STORE_PRICE_OPEN_TAB_KEY); } catch { /* 저장소 막힘 — 읽은 것도 없다 */ }
  }, []);  // 네이버 가격 → PriceHub 매장가(apply-current) — 한 번에 하나만. 적용한 카드는 목록을 다시 받을 때까지 숨긴다.
  const [applying, setApplying] = useState(false);
  const applyingRef = useRef(false);
  const [appliedIds, setAppliedIds] = useState<Set<number>>(() => new Set());
  const [failures, setFailures] = useState<ApplyFailure[] | null>(null);
  // 나눠 보내는 동안의 진행(보낸 장수 / 전체)과, 409 · 502 로 멈췄을 때 남은 카드(다시 시도).
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // store = 그 카드들을 받은 목록의 매장 — 다시 시도도 처음 고른 목록의 매장으로 보낸다(감사 #66).
  // after = 이 시각 뒤에 시작한 조회의 목록(PriceHub 응답)을 받기 전까지 다시 시도를 막는다(PriceHub 409 — 코덱 셀프 리뷰 낮음).
  const [retry, setRetry] = useState<{ ids: number[]; reason: string; store: string | null; after?: number } | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  // 요청 대기열(감사 card-controltower#71, 백엔드 PR #47) — 요청한 카드는 목록에서 빼지 않고 "PriceHub 확정 대기" 로 남긴다.
  // 목록을 다시 받기 전까지 화면에 덮어 두고, 다시 받으면 서버의 pendingRequest 가 그 자리를 잇는다.
  const [requested, setRequested] = useState<Map<number, HubPendingRequest>>(() => new Map());
  // 응답을 받지 못한 묶음의 카드(감사 #55, 코덱 셀프 리뷰 중간) — 서버는 처리했을 수 있어 목록을 다시 받을 때까지 다시 보내지
  // 않는다. 다시 받기가 늦거나 실패해도 줄 버튼 · 묶음 버튼으로 같은 요청이 또 나가지 않게.
  // 카드 id → 응답 불명을 적은 시각. 목록을 다시 받을 때 '그 뒤에 시작한' 조회가 낸 목록이면 지운다 — 요청 전에 시작한 조회가
  // 늦게 도착해 재전송을 다시 열던 것(코덱 리뷰 #209-1, 감사 #281). dataStartedAt 을 모르면(예전 호출) 예전처럼 모두 지운다.
  const [unknownIds, setUnknownIds] = useState<Map<number, number>>(() => new Map());
  const dataStartedAtRef = useRef(dataStartedAt);
  dataStartedAtRef.current = dataStartedAt;
  // 응답 불명 · 요청 대기 표시를 적은 목록의 매장 — 다른 매장 목록이 오면 카드 id 가 겹쳐도 그 표시를 옮기지 않는다.
  const marksStoreRef = useRef<string | null | undefined>(data?.store);
  useEffect(() => {
    setAppliedIds(new Set());
    const prevStore = marksStoreRef.current;
    if (data) marksStoreRef.current = data.store;
    const sameStore = !data || (data.store ?? null) === (prevStore ?? null);
    // PriceHub 장애로 로컬 대체 목록(pricehubAvailable=false)만 받았으면 요청 대기 · 결과를 확인하지 못한 것이다 — 같은 매장이면
    // 응답 불명 잠금과 방금 보낸 요청 표시를 그대로 둔다(감사 card-controltower#311: 시작 시각만 보고 풀어 같은 카드 재전송이 열렸다).
    if (sameStore && data && !data.pricehubAvailable) return;
    setRequested(new Map());
    const startedAt = dataStartedAtRef.current;
    setUnknownIds((prev) => {
      if (prev.size === 0) return prev;
      if (startedAt == null || !sameStore) return new Map();
      const next = new Map(Array.from(prev).filter(([, at]) => at > startedAt));
      return next.size === prev.size ? prev : next;
    });
  }, [data]);
  const markUnknown = useCallback((ids: number[]) => {
    const now = Date.now();
    setUnknownIds((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => next.set(id, now));
      return next;
    });
  }, []);

  // 목록을 다시 받으면 "다시 시도" 대상도 새 목록에 맞춘다(감사 card-controltower#77) — 다른 매장 목록이면 비우고, 같은
  // 매장이면 새 목록에 남아 있고 지금 보낼 수 있는 카드만 남긴다(다 빠지면 비움). 통째로 비우지 않는 것은 나눠 보내다
  // 멈춘 뒤의 목록 새로고침(앞 묶음 반영분)이 남은 카드의 다시 시도까지 지우지 않게 하려는 것이다.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  useEffect(() => {
    setRetry((prev) => {
      if (!prev) return prev;
      if ((data?.store ?? null) !== prev.store) return null;
      const sendable = new Set(
        rowsRef.current.filter((r) => !applyCurrentBlockReason(r)).map((r) => r.cardId as number),
      );
      const ids = prev.ids.filter((id) => sendable.has(id));
      if (ids.length === 0) return null;
      // 다시 받기를 기다리던 다시 시도는 그 뒤에 시작한 조회가 PriceHub 응답과 함께 왔을 때만 연다.
      const startedAt = dataStartedAtRef.current;
      const reloaded = prev.after != null && data?.pricehubAvailable === true && startedAt != null && startedAt > prev.after;
      if (reloaded) return { ...prev, ids, after: undefined };
      return ids.length === prev.ids.length ? prev : { ...prev, ids };
    });
  }, [data]);

  // 나눠 보내는 중의 중단 장치(코덱 리뷰 #105 높음) — http 는 요청마다 지금 매장(X-Store-Code)을 읽으므로, SUPER 가
  // 첫 묶음을 기다리는 동안 매장을 바꾸면 다음 묶음의 이전 매장 카드 id 가 새 매장으로 나간다. 세션(매장 · 계정)이 바뀌면
  // 기다리던 요청까지 끊고 결과를 버린다. 화면만 벗어나면(탭 전환 · 다른 메뉴 — Opus 리뷰 PR #43 중간 1) 다음 묶음은 보내지
  // 않되, 기다리던 묶음은 끝까지 받아 결과를 부모(살아 있는 가격 조회 화면)에 넘긴다 — 버리면 하락 확인 대기가 남는다.
  const runRef = useRef<{ ctrl: AbortController; session: string } | null>(null);
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      const run = runRef.current;
      if (run && sessionOf() !== run.session) run.ctrl.abort();
    };
  }, []);
  useEffect(() => {
    const run = runRef.current;
    if (run && sessionOf() !== run.session) run.ctrl.abort();
  }, [superStore, isLoggedIn, store, username]);

  const liveRows = useMemo(() => {
    const kept = appliedIds.size === 0 ? rows : rows.filter((r) => r.cardId == null || !appliedIds.has(r.cardId));
    if (requested.size === 0 && unknownIds.size === 0) return kept;
    return kept.map((r) => {
      if (r.cardId != null && unknownIds.has(r.cardId)) {
        return { ...r, canApplyCurrent: false, applyCurrentBlockReason: UNKNOWN_RESULT_REASON };
      }
      // 방금 보낸 요청이 받은 목록보다 새것이다 — 서버의 옛 대기(예: 규칙 제안 auto_rule)가 있어도 덮는다(코덱 셀프 리뷰 중간).
      const req = r.cardId != null ? requested.get(r.cardId) : undefined;
      return req
        // 출처도 '매장 가격 요청' 으로 — 옛 noticeKind(네이버 직접 변경 등)가 우선해 다시 받을 때까지 남지 않게(코덱 리뷰 잡 #318).
        ? { ...r, pendingRequest: req, noticeKind: 'apply_current', canApplyCurrent: false, applyCurrentBlockReason: LOCAL_PENDING_REASON }
        : r;
    });
  }, [rows, appliedIds, requested, unknownIds]);
  // 두 값이 같은 줄은 어디에도 보이지 않는다(설계 §1 — 부모가 buildHubSelection 으로 이미 빼지만 표만 쓰는 곳을 위해 한 번 더).
  const visible = useMemo(() => liveRows.filter((r) => hubRowAdvice(r) !== null), [liveRows]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  // 체크 차단 사유(할 일 안에서도 남는 것 — 지금 요청을 보낼 수 없는 줄).
  const blockOf = useCallback((r: HubRow) => hubRowBlockReason(r), []);
  // 보기별 줄 · 건수.
  const statusCounts = useMemo(() => {
    const c: Record<HubStatusTab, number> = { available: 0, pending: 0, blocked: 0 };
    visible.forEach((r) => { c[hubRowStatus(r)] += 1; });
    return c;
  }, [visible]);
  const tabRows = useMemo(() => visible.filter((r) => hubRowStatus(r) === statusTab), [visible, statusTab]);
  const selectable = statusTab === 'available';
  // 체크박스 · 선택 바는 요청할 수 있는 계정만(유저 플로우 제안 7) — 조회 전용은 요청 버튼이 없어 체크해도 할 일이 없는 막다른 길이었다.
  const checkable = selectable && isAdmin;
  // 전체 선택 = 이 보기의 줄 전부 — 머리 체크와 같은 범위(F4: '22건 중 3건' 이 생기지 않게).
  const visibleSelectable = useMemo(
    () => (selectable ? tabRows.filter((r) => !blockOf(r)).map((r) => r.cardId as number) : []),
    [selectable, tabRows, blockOf],
  );
  // 표 위 · 아래 페이지 넘김(감사 card-controltower#115 · #116) — 보기를 바꾸면 1페이지, 크기는 가격 조회 화면 표들과 함께 남긴다.
  const paging = useTablePaging(tabRows, statusTab);
  const pageSelectable = useMemo(
    () => (selectable ? paging.pageRows.filter((r) => !blockOf(r)).map((r) => r.cardId as number) : []),
    [selectable, paging.pageRows, blockOf],
  );
  const allPageSelected = pageSelectable.length > 0 && pageSelectable.every((id) => selectedSet.has(id));
  // 500줄 이상 페이지는 보이는 줄만 그린다(가상 스크롤, 1,000줄 한 번에 그리기가 약 1초 멈췄다). virtual 은 scroll.x 가
  // 숫자여야 해서 열 폭 합(columnsScrollX) · 휴대폰 한 열(340)을 준다.
  const virtualY = useVirtualScrollY(scrollY);
  const virtual = paging.pageSize >= VIRTUAL_FROM_PAGE_SIZE && paging.pageRows.length > 100;
  // 종류 · 시각 필터는 없앴다(설계 §3) — 부모에게 '거른 보기 없음' 을 알린다(예전 계약 그대로).
  useEffect(() => { onVisibleIdsChange?.(null); }, [onVisibleIdsChange]);

  // 보기를 바꾸면 선택을 비운다(감사 card-controltower#330 — 지금 보지 않고 고른 줄이 다른 보기 뒤에 남지 않게).
  const clearSelection = () => { if (selectedIds.length > 0) onSelectedIdsChange([]); };
  const changeStatusTab = (tab: HubStatusTab) => {
    if (tab === statusTab) return;
    if (statusTabProp === undefined) setOwnStatusTab(tab);
    onStatusTabChange?.(tab);
    clearSelection();
  };
  // 화면이 보기를 바꿨을 때(저장 칩)도 누른 것과 같게 선택을 비운다.
  const prevStatusTabRef = useRef(statusTab);
  useEffect(() => {
    if (prevStatusTabRef.current === statusTab) return;
    prevStatusTabRef.current = statusTab;
    if (selectedIds.length > 0) onSelectedIdsChange([]);
    // 보기가 바뀔 때만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab]);
  // ── 네이버 가격 → PriceHub 매장가 ──────────────────────────────────────
  const nameOf = useCallback((cardId: number) => {
    const r = rows.find((x) => x.cardId === cardId);
    return r ? (r.productName ?? r.sellerProductCode) : `카드 ${cardId}`;
  }, [rows]);

  /** 나눠 보내다 끊긴 뒤의 안내 — 로그아웃은 로그인 화면이 대신하므로 조용히. */
  const reportStopped = useCallback((s: ApplyStop, applied: ApplyCurrentApplied[], fromStore: string | null) => {
    if (s.reason === 'logout') return;
    const sentCount = applied.length;
    if (s.unsent === 0 && s.unknown === 0) {
      if (sentCount > 0) message.success(appliedSummary(applied));
      return;
    }
    const pendingN = applied.filter(isPendingApplied).length;
    const settledN = sentCount - pendingN;
    const sent = [
      settledN > 0 ? ` 앞의 ${settledN.toLocaleString()}건은 PriceHub 가격을 바꿨습니다.` : '',
      pendingN > 0 ? ` 앞의 요청 ${pendingN.toLocaleString()}건은 PriceHub 확인 중입니다.` : '',
    ].join('');
    const unknown = s.unknown > 0
      ? ` 응답을 기다리던 ${s.unknown.toLocaleString()}건은 반영됐을 수 있습니다 — 그 매장에서 새로고침으로 확인해 주세요.`
      : '';
    const head = s.reason === 'store'
      ? '매장이 바뀌어 중단했습니다'
      : s.reason === 'account' ? '로그인 계정이 바뀌어 중단했습니다' : '화면을 벗어나 중단했습니다';
    message.warning({
      content: `${head} — ${s.unsent.toLocaleString()}건은 보내지 않았습니다.${sent}${unknown}`,
      duration: 10,
    });
  }, []);

  /**
   * listStore = 이 카드들을 받은 hub-changes 목록의 매장(응답의 store) — 요청 본문에 실어 서버가 요청 매장과 대조한다(감사
   * card-controltower#66). 한 목록은 한 매장이라 묶음 안에 매장이 섞이지 않는다.
   */
  const applyCurrent = useCallback(async (ids: number[], listStore: string | null) => {
    if (ids.length === 0 || applyingRef.current) return; // 연타 · 중복 요청 막기
    applyingRef.current = true;
    setApplying(true);
    setRetry(null);
    const ctrl = new AbortController();
    // 시작할 때의 세션(역할 · 계정 매장 · SUPER 가 고른 매장 · 아이디) — 묶음마다 지금 값과 비교한다.
    const session = sessionOf();
    const run = { ctrl, session };
    runRef.current = run;
    const startStore = localStorage.getItem(STORAGE_KEY.superStore);
    const interrupted = (): ApplyStopReason | null => {
      if (!localStorage.getItem(STORAGE_KEY.role)) return 'logout';
      if (localStorage.getItem(STORAGE_KEY.superStore) !== startStore) return 'store';
      if (sessionOf() !== session) return 'account';
      if (unmountedRef.current || ctrl.signal.aborted) return 'unmount';
      return null;
    };
    let stopped: ApplyStop | null = null;
    const applied: ApplyCurrentApplied[] = [];
    const failed: { cardId: number; reason: string }[] = [];
    let answered = false;
    try {
      // 서버 한도(한 번에 APPLY_CURRENT_CHUNK 장, 넘으면 400)에 맞춰 나눠 순서대로 보낸다. 한 묶음이 실패하면 거기서 멈추고
      // 남은 것(그 묶음 포함)을 "다시 시도" 로 남긴다 — 앞 묶음은 이미 반영됐으므로 결과는 아래에서 그대로 처리한다.
      for (let i = 0; i < ids.length; i += APPLY_CURRENT_CHUNK) {
        const chunk = ids.slice(i, i + APPLY_CURRENT_CHUNK);
        const why = interrupted();
        if (why) { stopped = { reason: why, unsent: ids.length - i, unknown: 0 }; break; }
        if (ids.length > APPLY_CURRENT_CHUNK) setProgress({ done: i, total: ids.length });
        try {
          const res = await applyCurrentToHub(chunk, { signal: ctrl.signal, store: listStore });
          answered = true;
          applied.push(...res.applied);
          failed.push(...res.failed);
          // 이 묶음에 결과 불명(PriceHub 응답 타임아웃) 카드가 있으면 다음 묶음을 보내지 않는다 — 한 묶음이 실패하면 거기서
          // 멈추는 규칙(코덱 셀프 리뷰 중간). 결과 불명 카드 잠금 · 안내 · 재조회는 아래 끝에서 한다.
          const unsent = ids.slice(i + chunk.length);
          if (unsent.length > 0 && res.failed.some((f) => UNCLEAR_REASON.test(f.reason ?? ''))) {
            const sentNow = ` 앞의 ${(i + chunk.length).toLocaleString()}건은 보냈습니다.`;
            message.warning({
              content: `결과를 알 수 없는 카드가 있어 멈췄습니다 — 남은 ${unsent.length.toLocaleString()}건은 보내지 않았습니다.${sentNow}`,
              duration: 10,
            });
            setRetry({ ids: unsent, reason: `앞 묶음에 결과를 알 수 없는 카드가 있어 멈췄습니다 — 남은 카드는 보내지 않았습니다.${sentNow}`, store: listStore });
            break;
          }
        } catch (e) {
          // 기다리는 중에 끊겼으면(매장 전환 · 로그아웃 · 언마운트) 이 묶음은 서버가 받았는지 알 수 없다 — 이전 매장
          // 헤더로 나간 요청이라 다른 매장을 건드리지는 않는다.
          const whyNow = interrupted();
          if (whyNow) {
            stopped = { reason: whyNow, unsent: ids.length - i - chunk.length, unknown: chunk.length };
            break;
          }
          const status = statusOf(e);
          const rest = ids.slice(i);
          const sent = applied.length > 0 ? ` 앞의 ${applied.length.toLocaleString()}건은 보냈습니다.` : '';
          if (status === 409 && !fromPricehub(e)) {
            // 잡 락 409 — 서버 문구(가격 조회 · 재고 동기화 · 다른 확정 진행 중)를 그대로 보여 준다(감사 #80).
            const reason = serverMessage(e) ?? '다른 작업이 진행 중입니다';
            message.warning({ content: `${reason} — 끝난 뒤 다시 시도해 주세요.${sent}`, duration: 8 });
            setRetry({ ids: rest, reason: `${reason} — 끝난 뒤 다시 보내 주세요.${sent}`, store: listStore });
          } else if (status === 409) {
            // PriceHub 409(pricehubStatus) — 그 사이 값이 바뀌었거나 이미 처리된 요청이다. 잡 락과 달리 기다린다고 풀리지 않으니
            // 목록을 다시 받아 PriceHub 상태(요청 대기 · 바뀐 값)를 보이고, 다시 시도는 새 목록으로 걸러 지금 보낼 수 있는 카드만
            // 남긴다(감사 card-controltower#313 — 예전엔 "가격 조회 중 — 끝난 뒤 다시" 로 잘못 안내했다).
            const reason = serverMessage(e) ?? '그 사이 값이 바뀌었거나 이미 처리된 요청입니다';
            message.warning({
              content: `PriceHub 가 거절했습니다(${reason}) — 그 사이 값이 바뀌었거나 이미 처리된 요청입니다. 목록을 다시 받아 확인한 뒤 남은 카드만 보내 주세요.${sent}`,
              duration: 10,
            });
            setRetry({
              ids: rest,
              reason: `PriceHub 에서 값이 그 사이 바뀌었거나 이미 처리된 요청이라 멈췄습니다(${reason}) — 다시 받은 목록에서 확인한 뒤 보내 주세요.${sent}`,
              store: listStore,
              after: Date.now(),
            });
            // 앞 묶음이 응답했으면 아래 끝에서 한 번 다시 받는다(감사 #288 — 카드 전체 조회가 겹치지 않게).
            if (!answered) onReload();
          } else if (status === 502 && !resultUnknownOf(e)) {
            message.error({ content: `PriceHub 에 연결하지 못해 요청하지 못했습니다 — 잠시 뒤 다시 시도해 주세요.${sent}`, duration: 6 });
            setRetry({ ids: rest, reason: `PriceHub 에 연결하지 못했습니다.${sent}`, store: listStore });
          } else if (status === 401 || status === 403) {
            // http 인터셉터가 이미 안내한다(세션 만료 · 권한 없음).
          } else if (status == null || status === 502) {
            // 응답을 받지 못했다(시간 초과 · 연결 끊김) — 서버는 이미 처리했을 수 있다. 같은 카드를 다시 보내게 하지 않고 목록을
            // 다시 받아 실제 상태(확정 대기로 바뀌었는지)를 보게 한다(감사 card-controltower#55 — 응답 불명을 실패로 오감지).
            // 502 + resultUnknown=true(PriceHub 응답 타임아웃 — 서버는 PriceHub 에 보냈다)도 같다(감사 card-controltower#312).
            message.warning({
              content: status == null
                ? `응답을 받지 못했습니다 — 서버에서는 처리됐을 수 있어 목록을 다시 받아 확인합니다(받기 전까지 그 카드는 다시 보낼 수 없습니다).${sent}`
                : `결과 불명 — PriceHub 가 제때 응답하지 않아 요청이 들어갔는지 모릅니다. 목록을 다시 받아 요청 대기를 확인합니다(받기 전까지 그 ${chunk.length.toLocaleString()}건은 다시 보낼 수 없습니다).${sent}`,
              duration: 10,
            });
            markUnknown(chunk);
            // 아직 보내지 않은 뒤 묶음은 다시 시도로 남긴다(보낸 적이 없으니 겹치지 않는다).
            const unsent = ids.slice(i + chunk.length);
            if (unsent.length > 0) {
              setRetry({ ids: unsent, reason: `앞 묶음의 응답을 받지 못해 멈췄습니다 — 남은 카드는 보내지 않았습니다.${sent}`, store: listStore });
            }
            // 앞 묶음이 응답했으면 아래 끝에서 한 번 다시 받는다 — 여기서 또 부르면 카드 전체 조회가 겹쳤다(코덱 리뷰 #209-8, 감사 #288).
            if (!answered) onReload();
          } else {
            message.error({ content: serverMessage(e) ?? '요청을 보내는 중 오류가 발생했습니다.', duration: 6 });
          }
          break;
        }
      }
      if (!stopped) {
        const whyEnd = interrupted();
        if (whyEnd) stopped = { reason: whyEnd, unsent: 0, unknown: 0 };
      }
      if (stopped) {
        // 세션(매장 · 계정)이 바뀌었으면 결과를 지금 화면에 덮지 않고 안내만 한다. 화면만 벗어났으면 세션은 그대로라 받은 결과를
        // 부모에 넘기고(카드 값 · 하락 확인 대기 정리) 목록을 새로 받게 한다.
        if (stopped.reason === 'unmount') {
          if (applied.length > 0) onAppliedCurrent?.(applied);
          if (answered) onReload();
        }
        reportStopped(stopped, applied, startStore);
        return;
      }

      // 요청(확정 대기)한 줄은 목록에 남기고 "PriceHub 확정 대기" 로 바꾼다(감사 card-controltower#71) — 매장가는 PriceHub
      // 관리자가 확정해야 바뀐다. 예전 응답(pending 없음) · 이미 같은 값(already_same)은 지금처럼 바로 반영된 것으로 본다:
      // CT 카드까지 맞춰진 것만 목록에서 뺀다 — warning 이 있으면 CT 수정가가 그대로일 수 있어 다시 받을 때까지 둔다.
      const requestedNow = applied.filter(isPendingApplied);
      if (requestedNow.length > 0) {
        const at = new Date().toISOString();
        setRequested((prev) => {
          const next = new Map(prev);
          requestedNow.forEach((a) => next.set(a.cardId, {
            kind: 'apply_current', requestedBy: username ?? null, at, price: a.hubPrice,
          }));
          return next;
        });
      }
      const clean = applied.filter((a) => !isPendingApplied(a) && !a.warning).map((a) => a.cardId);
      if (clean.length > 0) setAppliedIds((prev) => new Set([...Array.from(prev), ...clean]));
      if (applied.length > 0) {
        onAppliedCurrent?.(applied);
        message.success(appliedSummary(applied));
      }
      const warned = applied.filter((a) => a.warning);
      if (warned.length > 0) {
        const first = `${nameOf(warned[0].cardId)}: ${warned[0].warning}`;
        message.warning({
          content: warned.length === 1 ? first : `${first} 외 ${warned.length - 1}건`,
          duration: 8,
        });
      }
      if (failed.length > 0) {
        setFailures(failed.map((f) => ({ cardId: f.cardId, name: nameOf(f.cardId), reason: f.reason })));
        // PriceHub 쪽 시간 초과로 실패 처리된 카드는 실제로는 반영됐을 수 있다 — 다시 받은 목록에서 확인하라고 알린다(#55).
        const unclear = failed.filter((f) => UNCLEAR_REASON.test(f.reason ?? ''));
        if (unclear.length > 0) {
          // 그 카드도 다시 받기 전까지 다시 보내지 않는다(코덱 리뷰 #209-2, 감사 #282).
          markUnknown(unclear.map((f) => f.cardId));
          message.info({
            content: `${unclear.length.toLocaleString()}건은 PriceHub 응답이 늦어 결과를 알 수 없습니다 — 실제로는 반영됐을 수 있어 다시 받은 목록에서 확인해 주세요.`,
            duration: 10,
          });
        }
      }
      if (answered) onReload();
    } finally {
      if (runRef.current === run) runRef.current = null;
      applyingRef.current = false;
      setApplying(false);
      setProgress(null);
    }
  }, [nameOf, onAppliedCurrent, onReload, reportStopped, username, markUnknown]);

  const labelOf = (r: HubRow) => r.productName ?? r.sellerProductCode;
  // 값 이름은 용어 사전 둘(네이버 판매가 · PriceHub 가격, 사장 확정 2026-11-04 15:45 — 예전 '광주 가격' 같은 <매장> 가격 이름은 쓰지 않는다).
  const helpText = hubHelpText();

  /**
   * "남은 N건 다시 시도"(감사 card-controltower#77) — 멈춘 뒤 목록 · 카드 상태가 바뀌었을 수 있어 옛 id 를 그대로 보내지
   * 않는다. 지금 목록에 있고 보낼 수 있는 카드만 다시 골라(applyCurrentBlockReason) 건수 · 방향을 적은 확인 창을 거친다.
   */
  // 확인 창이 열린 동안 목록을 다시 받을 수 있어(잡 종료 · 새로고침) 확인을 누를 때 지금 값으로 한 번 더 거른다.
  const retryRef = useRef(retry);
  retryRef.current = retry;
  const liveRowsRef = useRef(liveRows);
  liveRowsRef.current = liveRows;
  const sendableRetryIds = (r: NonNullable<typeof retry>, list: HubRow[]) => {
    const byId = new Map(list.filter((x) => x.cardId != null).map((x) => [x.cardId as number, x]));
    return r.ids.filter((id) => {
      const row = byId.get(id);
      return !!row && hubRowStatus(row) === 'available' && !applyCurrentBlockReason(row);
    });
  };
  const noneToRetry = () => {
    setRetry(null);
    message.info('남은 카드 중 지금 요청할 수 있는 카드가 없습니다 — 목록을 새로 확인해 주세요.');
  };

  const retryApplyCurrent = () => {
    if (!retry || retry.after != null) return;
    const ids = sendableRetryIds(retry, liveRows);
    if (ids.length === 0) { noneToRetry(); return; }
    const dropped = retry.ids.length - ids.length;
    const retryStore = retry.store;
    setPending({
      title: '다시 요청',
      description: `남은 ${ids.length.toLocaleString()}건 — PriceHub 가격을 각 카드의 네이버 판매가로 맞춰 달라고 PriceHub 에 요청합니다(PriceHub 관리자가 확인한 뒤 바뀌고, 네이버 판매가는 그대로).${
        dropped > 0 ? ` 목록에서 빠졌거나 지금 보낼 수 없는 ${dropped.toLocaleString()}건은 빠집니다.` : ''}`,
      okText: 'PriceHub 에 요청',
      run: () => {
        // 창이 열린 동안 다른 매장 목록을 받아 다시 시도가 닫혔거나 대상이 바뀌었으면 지금 값으로 다시 거른다.
        const now = retryRef.current;
        if (!now || now.store !== retryStore) { noneToRetry(); return; }
        const nowIds = sendableRetryIds(now, liveRowsRef.current);
        if (nowIds.length === 0) { noneToRetry(); return; }
        applyCurrent(nowIds, retryStore);
      },
    });
  };

  // ── 컬럼(설계 2026-11-04 §2-3: 이미지 · 상품 · 재고 · 네이버 판매가 · PriceHub 가격 · 차이 · 지금 할 일 · 처리) ──────────────
  const adviceOf = (r: HubRow) => hubRowAdvice(r);
  const columns: ColumnsType<HubRow> = [
    {
      // 카드 이미지(감사 card-controltower#439) — 상품 칸 앞 56×78, 누르면 크게.
      title: '이미지', key: 'thumb', width: 64, align: 'center',
      render: (_, r) => <HubCardThumb url={r.imageUrl} name={r.productName ?? r.sellerProductCode} width={56} t={t} />,
    },
    {
      title: '상품', key: 'name', minWidth: wide ? 150 : 130, ellipsis: { showTitle: false },
      render: (_, r) => (
        <div data-testid="hub-cell-name" style={ONE_LINE} title={`${r.productName ?? 'CT 에 없는 상품'} · ${r.sellerProductCode}`}>
          <span style={{ fontWeight: FONT_WEIGHT.medium, color: r.productName ? t.textPrimary : t.textMuted }}>
            {r.productName ?? 'CT 에 없는 상품'}
          </span>
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, marginLeft: SPACING.xs }}>{r.sellerProductCode}</span>
        </div>
      ),
    },
    {
      // 네이버 재고(감사 card-controltower#348) — 스토어 상품 관리와 같은 출처(CT 카드 목록). 0 은 품절이라 빨갛게.
      title: '재고', key: 'stock', width: 52, align: 'right',
      render: (_, r) => <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}><StockCell stock={r.stockQuantity} t={t} /></span>,
    },
    {
      // 머리는 한 줄(배치 규칙 K1 · 감사 card-controltower#878 — 예전엔 1280 에서 두 줄로 접었다). 금액 열 폭은 머리 '네이버 판매가 (원)' 이
      // 아이콘과 한 줄로 들어가는 TABLE_COL.moneyTwoLine(144).
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          <ShopOutlined aria-hidden style={{ color: t.textMuted, marginRight: 4 }} />{numberHeader('네이버 판매가')}
        </span>
      ),
      key: 'naverPrice', width: TABLE_COL.moneyTwoLine, align: 'right',
      render: (_, r) => (
        <div data-testid="hub-cell-naver" style={PRICE_CELL}>
          {/* 단위(원)는 열 머리, 값이 없으면 CT 에 없는 상품은 해당 없음(-) · 있는 상품은 미수집(DS-1 NumberCell). */}
          <span style={NOWRAP}>
            <NumberCell value={r.currentPrice} color={t.textSecondary} missing={r.cardId == null ? 'na' : 'uncollected'} />
            {adviceOf(r)?.base === 'naver' && <BaseTag t={t} />}
          </span>
        </div>
      ),
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          <ClusterOutlined aria-hidden style={{ color: t.accent, marginRight: 4 }} />{numberHeader('PriceHub 가격')}
        </span>
      ),
      key: 'hubPrice', width: TABLE_COL.moneyTwoLine, align: 'right',
      render: (_, r) => (
        // 시각(예전 '출처 · 시각' 칸)은 title 로(설계 §3).
        <div data-testid="hub-cell-hub" style={PRICE_CELL} title={r.at ? `PriceHub ${dayjs(r.at).format('MM/DD HH:mm')}` : undefined}>
          <span style={NOWRAP}>
            <NumberCell value={priceTarget(r)} bold color={t.accent} />
            {adviceOf(r)?.base === 'hub' && <BaseTag t={t} />}
          </span>
        </div>
      ),
    },
    {
      title: '차이', key: 'diff', width: 112, align: 'right',
      render: (_, r) => <DiffCell naver={r.currentPrice} hub={priceTarget(r)} t={t} />,
    },
    {
      title: '지금 할 일', key: 'advice', minWidth: 240,
      render: (_, r) => <AdviceCell r={r} a={adviceOf(r)} store={data?.store} t={t} />,
    },
  ];

  /**
   * 처리 버튼(ADMIN/SUPER) — 둘뿐(설계 §2-3): [반영](네이버 판매가를 PriceHub 가격으로 — 가격 조회 ③ 으로 간다) · [요청](PriceHub 가격을
   * 네이버 판매가로 바꿔 달라고 — apply-current). 추천 쪽 채움 · 다른 쪽 외곽선, 확인 필요는 둘 다 외곽선, 가격 받기 먼저는 [가격 받기] · [요청].
   * 버튼 글은 한 단어(#560), 금액 · 방향 전체는 title · 읽어 주는 이름 · 확인 창.
   */
  const renderActions = (r: HubRow) => {
    const a = adviceOf(r);
    if (!a || a.kind === 'checking' || a.kind === 'excluded') return null;
    const name = labelOf(r);
    const naver = r.currentPrice as number;
    const hub = priceTarget(r) as number;
    const requestBlock = applyCurrentBlockReason(r) ?? (applying ? '다른 카드를 보내는 중입니다' : undefined);
    const requestLabel = `요청 — PriceHub 가격을 ${won(hub)} → ${won(naver)} 으로 바꿔 달라고 PriceHub 관리자에게`;
    const reflectLabel = a.kind === 'fetchFirst'
      ? '가격 받기 — 가격 조회 ① 에서 PriceHub 가격을 받은 뒤 반영할 수 있습니다'
      : `반영 — 네이버 판매가를 ${won(naver)} → ${won(hub)} 으로(가격 조회 ③ 스토어 반영)`;
    const recommend = a.kind === 'apply' ? 'reflect' : a.kind === 'request' ? 'request' : null;
    return (
      <div data-testid="hub-actions" style={{
        display: 'flex', alignItems: 'center', justifyContent: phone ? 'flex-start' : 'flex-end', gap: SPACING.xxs, flexWrap: 'wrap',
      }}>
        <AppButton
          size="sm" variant={recommend === 'reflect' ? 'primary' : 'default'} style={tap}
          title={phone ? undefined : reflectLabel}
          aria-label={reflectLabel}
          data-testid="hub-reflect"
          onClick={() => setPending(a.kind === 'fetchFirst'
            ? {
              title: '가격 받기',
              description: `${name} — PriceHub 가격이 ${won(hub)} 으로 바뀌었지만 CT 가 아직 받지 않았습니다. 가격 조회 화면의 ① 가격 받기 뒤 ③ 에서 이 카드를 반영합니다(가격 조회 표에서 이 카드를 찾아 보여 줍니다).`,
              okText: '가격 조회로',
              run: () => openInPriceLookup(r),
            }
            : {
              title: '네이버 판매가 반영',
              // ③ 은 반영 대상 전체를 한 번에 보낸다(카드 하나만 보내는 버튼은 없다) — 그 카드를 찾아 보여 주고 그 사실을 함께 적는다(코덱 리뷰 #869).
              description: `${name} — 네이버 판매가를 ${won(naver)} → ${won(hub)} 으로 바꿉니다. 가격 조회 ③ 에서 이 카드를 반영합니다${
                naver > hub ? '(내림은 ② 하락 검토를 먼저 거칩니다)' : ''} — 가격 조회 표에서 이 카드를 찾아 보여 주고, ③ 스토어 반영은 반영 대상 전체를 함께 보냅니다.`,
              okText: '가격 조회로',
              run: () => openInPriceLookup(r),
            })}
        >
          {a.kind === 'fetchFirst' ? '가격 받기' : '반영'}
        </AppButton>
        <AppButton
          size="sm" variant={recommend === 'request' ? 'primary' : 'default'}
          style={recommend === 'request' ? tap : { ...tap, borderColor: t.lineAccent, color: t.accent }}
          title={phone ? undefined : requestLabel}
          aria-label={requestLabel}
          disabled={!!requestBlock} disabledReason={requestBlock}
          onClick={() => setPending({
            title: 'PriceHub 가격 요청',
            description: `${name} — PriceHub 가격을 ${won(hub)} → ${won(naver)} 으로 바꿔 달라고 PriceHub 관리자에게 보냅니다. `
              + '확인되기 전까지 이 카드는 반영하지 않습니다. 네이버 판매가는 그대로입니다.',
            okText: 'PriceHub 에 요청',
            run: () => applyCurrent([r.cardId as number], data?.store ?? null),
          })}
        >
          요청
        </AppButton>
      </div>
    );
  };

  // 휴대폰: 한 열 카드형(설계 §2-3 390) — 상품 / 지금 할 일(기준 · 바뀌는 값) / 두 값 · 재고 / [반영] [요청].
  const phoneColumns: ColumnsType<HubRow> = [{
    title: '상품 · 가격', key: 'card',
    render: (_, r) => (
      <div style={{ display: 'flex', gap: SPACING.xs, minWidth: 0 }}>
        <HubCardThumb url={r.imageUrl} name={r.productName ?? r.sellerProductCode} width={40} t={t} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xxs, minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: FONT_WEIGHT.medium, color: r.productName ? t.textPrimary : t.textMuted, wordBreak: 'keep-all' }}>
            {r.productName ?? 'CT 에 없는 상품'}
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{r.sellerProductCode}</div>
          <AdviceCell r={r} a={adviceOf(r)} store={data?.store} t={t} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: `0 ${SPACING.sm}px`, fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
            <span style={NOWRAP}>
              <ShopOutlined aria-hidden style={{ color: t.textMuted, marginRight: 4 }} />
              {`네이버 판매가 ${formatNumber(r.currentPrice, { withUnit: true, missing: r.cardId == null ? 'na' : 'uncollected' })}`}
            </span>
            <span style={{ ...NOWRAP, color: t.accent }}>
              <ClusterOutlined aria-hidden style={{ marginRight: 4 }} />
              {`PriceHub 가격 ${formatNumber(priceTarget(r), { withUnit: true })}`}
            </span>
            <span style={NOWRAP}>재고 <StockCell stock={r.stockQuantity} t={t} /></span>
          </div>
          {isAdmin && selectable && <div style={{ marginTop: SPACING.xxs }}>{renderActions(r)}</div>}
        </div>
      </div>
    ),
  }];

  if (isAdmin && selectable) {
    // 처리 열은 오른쪽에 고정 — 1280 에서 버튼이 표 밖으로 잘렸다(2026-10-21 코덱 화면 점검). 확인 중 · 대상 아님은 '지금 할 일' 칸에 글로.
    columns.push({
      title: '처리', key: 'action', width: ACTION_WIDTH, align: 'right', fixed: 'right',
      render: (_, r) => renderActions(r),
    });
  } else if (!selectable) {
    // 확인 중 · 대상 아님 보기는 '지금 할 일' 칸이 글 칸 — 넓게.
    const last = columns[columns.length - 1] as { minWidth?: number; width?: number };
    last.minWidth = INFO_WIDTH;
  }
  // 열 폭 합을 표 최소 폭으로 — 'max-content' 면 상품명이 줄지 않고 표가 늘어 말줄임이 먹지 않고 처리 열이 다른 칸을 덮었다(#326).
  // 체크 칸(48)은 요청 가능 탭에만 있다 — 다른 탭에서 더하면 표가 그만큼 넓어져 1280 에서 오른쪽 고정 칸이 종류를 덮었다(#517).
  const tableScrollX = columnsScrollX(columns as Array<{ width?: number | string; minWidth?: number }>, checkable ? 48 : 0);

  // ── 체크박스 — 카드 id 기준(화면이 가진 선택, 감사 #330). 다른 페이지의 선택은 유지하고, 필터를 바꾸면 비운다(위). ──────────
  const keyToCardId = useMemo(() => {
    const m = new Map<React.Key, number>();
    liveRows.forEach((r) => { if (r.cardId != null) m.set(r.key, r.cardId); });
    return m;
  }, [liveRows]);
  // 지금 페이지의 카드 — 체크 변경은 이 페이지 줄만 바꾸고, 다른 페이지 · 필터로 가려진 선택은 그대로 둔다.
  const pageCardIds = useMemo(
    () => new Set(paging.pageRows.filter((r) => r.cardId != null).map((r) => r.cardId as number)),
    [paging.pageRows],
  );

  const rowSelection: TableRowSelection<HubRow> = {
    selectedRowKeys: paging.pageRows
      .filter((r) => !blockOf(r) && selectedSet.has(r.cardId as number))
      .map((r) => r.key),
    onChange: (keys) => {
      const kept = selectedIds.filter((id) => !pageCardIds.has(id));
      const picked = keys.map((k) => keyToCardId.get(k)).filter((v): v is number => v != null);
      onSelectedIdsChange(Array.from(new Set([...kept, ...picked])));
    },
    getCheckboxProps: (r) => ({
      disabled: !!blockOf(r),
      name: `${r.productName ?? r.sellerProductCode} 선택`,
    }),
    renderCell: (_checked, r, _i, node) => {
      const reason = blockOf(r);
      if (!reason) return node;
      // 비활성 체크박스는 키보드로 닿지 않아 사유 툴팁을 마우스로만 볼 수 있었다(감사 card-controltower#78) — 감싼
      // 칸을 Tab 으로 닿게 하고, 포커스에도 툴팁을 열며, 사유를 칸 이름으로 읽힌다. aria-describedby 는 Tooltip 이
      // 자식에 덮어쓰므로(열렸을 때만 툴팁 id) 쓰지 않는다.
      return (
        <Tooltip title={reason} trigger={['hover', 'focus']}>
          <span tabIndex={0} role="group" aria-label={`선택할 수 없음 — ${reason}`} style={{ display: 'inline-flex' }}>
            {node}
          </span>
        </Tooltip>
      );
    },
    columnWidth: phone ? 52 : 48,
  };

  // 범위 두 갈래(감사 card-controltower#696, 설계 §2-2) — "이 페이지 N개"(머리 체크와 같은 범위) · "검색 결과 전체 N개"(이 탭의
  // 필터를 통과한 체크 가능 줄 전부). 서로 다른 버튼이라 누른 범위가 문구에 그대로 남는다. 해제는 전부.
  const selectPage = () => onSelectedIdsChange(Array.from(new Set([...selectedIds, ...pageSelectable])));
  const selectAllResults = () => onSelectedIdsChange(Array.from(new Set([...selectedIds, ...visibleSelectable])));
  const clearAll = () => { if (selectedIds.length > 0) onSelectedIdsChange([]); };
  // 요청 대상은 '할 일' 줄 중 고른 것만(보기를 바꾸면 선택을 비운다).
  const selectedRows = visible.filter((r) => hubRowStatus(r) === 'available' && !blockOf(r) && selectedSet.has(r.cardId as number));
  const selectedCount = selectedRows.length;
  // 체크했지만 지금 페이지 밖에 있는 줄 — 페이지 줄에 "다른 페이지 N건 선택"(감사 card-controltower#116).
  const otherPageSelected = selectedCount - paging.pageRows.filter((r) => !blockOf(r) && selectedSet.has(r.cardId as number)).length;
  const applicableRows = selectedRows.filter((r) => !applyCurrentBlockReason(r));
  const applicableIds = applicableRows.map((r) => r.cardId as number);
  // 제외 사유 — 고른 것 중 보낼 수 없는 줄(사유별).
  const bulkExcluded = groupExcluded(selectedRows.map((r) => applyCurrentExcludeLabel(r)));
  const allResultsSelected = visibleSelectable.length > 0 && visibleSelectable.every((id) => selectedSet.has(id));
  const bulkCurrentBlock = selectedCount === 0
    ? '보낼 카드를 선택해 주세요'
    : applicableIds.length === 0
      ? '선택한 카드 중 요청할 수 있는 카드가 없습니다(네이버 판매가 없음 · 대상 아닌 게임 등)'
      : applying ? '보내는 중입니다' : undefined;

  // 보기 칩 · 새로고침이 같이 쓰는 상자 높이(감사 #517).
  const FILTER_H = phone ? 44 : 32;

  return (
    <div style={{ '--hub-bar-h': `${phone ? 0 : barH}px` } as React.CSSProperties}>
      {/* 설명 띠 — 한 줄 요약 + '설명 보기'. 긴 설명은 펼칠 때만 아래 줄에(감사 #517). 오른쪽 위 건수 문장은 없앴다(설계 §3 — 칩 건수가 같은 말). */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: `${SPACING.xxs}px ${SPACING.sm}px`, flexWrap: 'wrap',
        padding: `${SPACING.xxs}px ${SPACING.lg}px`, background: t.bgRaised, borderBottom: `1px solid ${t.border}`,
        color: t.textSecondary, fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.relaxed,
      }}>
        <div data-testid="hub-help-summary" title={helpText} style={{ ...ONE_LINE, flex: '1 1 240px' }}>
          {hubHelpSummary()}
        </div>
        <AppButton
          variant="link" style={{ ...linkStyle, flexShrink: 0 }}
          aria-expanded={helpOpen} aria-controls="hub-help-text"
          onClick={() => setHelpOpen((v) => !v)}
        >
          {helpOpen ? '설명 접기' : '설명 보기'}
        </AppButton>
        <div id="hub-help-text" data-testid="hub-help" hidden={!helpOpen} style={{ flexBasis: '100%', minWidth: 0 }}>
          {helpText}
        </div>
      </div>

      {data && !data.pricehubAvailable && (
        <div role="status" style={{
          display: 'flex', alignItems: 'flex-start', gap: SPACING.xs,
          padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.tintWarning, borderBottom: `1px solid ${t.lineWarning}`,
          color: t.textPrimary, fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.relaxed,
        }}>
          <WarningOutlined style={{ color: t.warning, marginTop: 3 }} />
          <span>
            PriceHub 에 연결하지 못해 CT 가 받아 둔 PriceHub 가격으로만 비교합니다. PriceHub 가 방금 바꾼 가격과 PriceHub 확인 중인 카드는
            PriceHub 가 돌아오면 새로고침으로 볼 수 있습니다.
          </span>
        </div>
      )}
      {data?.pricehubTruncated && (
        <div role="status" style={{
          padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.tintInfo, borderBottom: `1px solid ${t.lineAccent}`,
          color: t.textPrimary, fontSize: FONT_SIZE.xs,
        }}>
          PriceHub 변경 기록이 많아 일부만 받았습니다 — CT 가 받아 둔 PriceHub 가격과 다른 카드는 빠짐없이 보입니다.
        </div>
      )}

      {/* 보기 칩 셋(설계 §3 — 탭 3 · 필터 칩 6 · 'CT 에 없는 상품' 스위치 대신): 할 일(기본) · PriceHub 확인 중 · 대상 아님. */}
      <div data-testid="hub-filter-row" style={{
        display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap',
        padding: `${SPACING.xs}px ${SPACING.lg}px`, borderBottom: `1px solid ${t.border}`,
      }}>
        <div role="group" aria-label="보기" style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap' }}>
          {(['available', 'pending', 'blocked'] as HubStatusTab[]).map((tab) => {
            const on = tab === statusTab;
            return (
              <button
                key={tab}
                type="button"
                aria-pressed={on}
                data-testid={`hub-view-${tab}`}
                onClick={() => changeStatusTab(tab)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: SPACING.xxs, height: FILTER_H, boxSizing: 'border-box',
                  padding: `0 ${SPACING.sm}px`, borderRadius: RADIUS.xl, cursor: 'pointer', whiteSpace: 'nowrap',
                  fontSize: FONT_SIZE.sm, fontVariantNumeric: 'tabular-nums', lineHeight: LINE_HEIGHT.none,
                  fontWeight: on ? FONT_WEIGHT.bold : FONT_WEIGHT.medium,
                  color: on ? t.accent : t.textSecondary, background: on ? t.tintInfo : t.bgSurface,
                  border: `1px solid ${on ? t.lineAccent : t.border}`,
                }}
              >
                {HUB_STATUS_LABEL[tab]} {statusCounts[tab].toLocaleString()}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        <AppButton size="sm" style={{ height: FILTER_H }} icon={<ReloadOutlined />} onClick={onReload} loading={loading}>새로고침</AppButton>
      </div>
      {/* 일괄 작업 바(감사 card-controltower#696, 설계 §2-2) — 1건 이상 고를 때만 보인다(결정 D5). 넓은 화면은 화면 위(헤더 아래)에
          붙어 목록을 내려도 보이고(#326), 휴대폰 폭은 화면 아래에 고정. 실행은 미리보기(전후 값 · 제외 사유 · 되돌리기)를 거친다(D6).
          이 화면은 요청 전용이라 버튼은 'PriceHub 에 요청' 하나 — 네이버 반영(③)은 가격 조회에서만(규칙 22 · 안 A). */}
      {checkable ? (
        <BulkActionBar
          ariaLabel="선택 카드 처리"
          selectedCount={selectedCount}
          applicableCount={applicableIds.length}
          excluded={bulkExcluded}
          page={{ count: pageSelectable.length, allSelected: allPageSelected, onSelect: selectPage }}
          all={{ count: visibleSelectable.length, allSelected: allResultsSelected, onSelect: selectAllResults }}
          onClear={clearAll}
          sticky
          phone={phone}
          forceVisible={!!progress}
          onHeightChange={setBarH}
          actions={isAdmin && (
            <AppButton
              size="sm" variant="primary" style={tap} loading={applying}
              icon={<ClusterOutlined aria-hidden />}
              disabled={!!bulkCurrentBlock} disabledReason={bulkCurrentBlock}
              onClick={() => setBulkPreview({
                rows: applicableRows,
                excluded: bulkExcluded,
                store: data?.store ?? null,
              })}
            >
              {`PriceHub 에 요청 (${applicableIds.length.toLocaleString()})`}
            </AppButton>
          )}
        >
          {progress && (
            <span role="status" style={{ textAlign: 'right', fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
              {`PriceHub 로 보내는 중 ${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}건`}
            </span>
          )}
        </BulkActionBar>
      ) : (
        <div role="note" data-testid="hub-tab-hint" style={{
          padding: `${SPACING.xs}px ${SPACING.lg}px`, borderBottom: `1px solid ${t.border}`,
          fontSize: FONT_SIZE.xs, color: t.textSecondary,
        }}>
          {!isAdmin
            ? `${HUB_STATUS_LABEL[statusTab]} ${tabRows.length.toLocaleString()}건 — ${READ_ONLY_REASON}(요청은 관리자가 합니다)`
            : `${HUB_STATUS_LABEL[statusTab]} ${tabRows.length.toLocaleString()}건 — 반영 · 요청은 '할 일' 에서`}
        </div>
      )}
      {isAdmin && retry && selectable && (
        <div role="alert" style={{
          display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap',
          padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.tintWarning, borderBottom: `1px solid ${t.lineWarning}`,
          color: t.textPrimary, fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.relaxed,
        }}>
          <WarningOutlined style={{ color: t.warning }} />
          <span style={{ flex: 1, minWidth: 0 }}>{retry.reason}</span>
          <AppButton
            size="sm" variant="primary" style={tap} loading={applying}
            disabled={retry.after != null}
            disabledReason="목록을 다시 받은 뒤 다시 시도할 수 있습니다"
            onClick={retryApplyCurrent}
          >
            {`남은 ${retry.ids.length.toLocaleString()}건 다시 시도`}
          </AppButton>
          <AppButton variant="link" style={linkStyle} onClick={() => setRetry(null)}>닫기</AppButton>
        </div>
      )}

      {error && data && <AppLoadError inline message={error.message} error={error} onRetry={onReload} />}

      {/* 첫 조회 중(받은 목록 없음)은 스켈레톤 줄(가격 목록 개편 §2-6 — 하락 검토 표와 같은 TableSkeleton, 감사 card-controltower#747).
          재조회는 지금처럼 기존 줄 위에 표의 회전 표시 — 요청 버튼이 옛 줄로 눌리지 않게 막는다. */}
      {error && !data ? (
        <AppLoadError message={error.message} error={error} onRetry={onReload} />
      ) : loading && !data ? (
        <TableSkeleton t={t} testId="hub-skeleton" />
      ) : (
        <>
        <TablePager paging={paging} placement="top" otherPageSelected={otherPageSelected} label="PriceHub 변경" className="hub-changes-pager" />
        <Table<HubRow>
          className={phone ? 'hub-changes-table' : 'hub-changes-table hub-changes-table--dense'}
          rowKey="key"
          size="small"
          columns={phone ? phoneColumns : columns}
          dataSource={paging.pageRows}
          rowSelection={checkable ? rowSelection : undefined}
          loading={loading}
          pagination={false}
          virtual={virtual}
          // 가상 스크롤이 아니면 표 안 세로 스크롤 없이 페이지와 함께 내려가고, 머리줄은 선택 줄 바로 아래에 붙는다(#326 — top 은
          // index.css 가 --app-sticky-top + --hub-bar-h 로 덮는다). 휴대폰은 카드형이라 붙이지 않는다.
          sticky={!virtual && !phone ? { offsetHeader: LAYOUT.headerHeight } : undefined}
          scroll={virtual
            ? { y: virtualY, x: phone ? 340 : tableScrollX }
            : phone ? undefined : { x: tableScrollX }}
          locale={{
            emptyText: (
              <TableEmptyState
                icon="✓"
                message={visible.length === 0
                  ? '네이버 판매가와 PriceHub 가격이 다른 카드가 없습니다'
                  : statusTab === 'available'
                    ? '지금 할 일이 없습니다'
                    : statusTab === 'pending'
                      ? 'PriceHub 확인 중인 카드가 없습니다'
                      : '대상 아닌 카드가 없습니다'}
                t={t}
              />
            ),
          }}
        />
        <TablePager paging={paging} placement="bottom" otherPageSelected={otherPageSelected} label="PriceHub 변경" />
        </>
      )}

      <AppModal
        title={pending?.title}
        open={!!pending}
        onCancel={() => setPending(null)}
        okText={pending?.okText}
        cancelText="취소"
        onOk={() => {
          const run = pending?.run;
          setPending(null);
          run?.();
        }}
        width={420}
      >
        <div style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary, lineHeight: LINE_HEIGHT.relaxed }}>
          {pending?.description}
        </div>
      </AppModal>

      {/* 일괄 요청 미리보기(결정 D6 — 건수와 상관없이 늘 거친다). 대상은 연 순간 고정하고, 보낼 때 지금 목록으로 한 번 더 거른다
          (연 동안 다시 받은 목록에서 확정 대기 · 응답 불명 잠금이 된 줄은 빠진다). */}
      <BulkPreviewModal
        open={!!bulkPreview}
        title="PriceHub 가격 요청"
        items={(bulkPreview?.rows ?? []).map((r) => ({
          key: r.key, name: r.productName ?? r.sellerProductCode, from: priceTarget(r), to: r.currentPrice,
        }))}
        fromLabel="PriceHub 가격"
        toLabel="요청(네이버 판매가)"
        excluded={bulkPreview?.excluded}
        description={bulkPreview ? bulkRequestText(bulkPreview.rows) : undefined}
        revertNote="CT 에서 보낸 요청을 거둘 수는 없습니다. PriceHub 관리자가 확인하기 전에는 PriceHub 가격이 바뀌지 않고, 관리자가 무시하면 바뀌지 않습니다."
        okText={`PriceHub 에 요청 (${(bulkPreview?.rows.length ?? 0).toLocaleString()})`}
        onCancel={() => setBulkPreview(null)}
        onOk={() => {
          const p = bulkPreview;
          setBulkPreview(null);
          if (!p) return;
          // 미리보기에 보인 값(스냅샷)과 지금 목록을 대조한다(코덱 리뷰 #715) — 값이 바뀐 · 빠진 카드는 보내지 않고 알린다.
          const { ids, changed, gone } = previewSendable(p.rows, liveRowsRef.current);
          if (changed + gone > 0) {
            const parts = [
              changed > 0 ? `미리보기 뒤 값이 바뀐 ${changed.toLocaleString()}건` : '',
              gone > 0 ? `목록에서 빠졌거나 지금 보낼 수 없는 ${gone.toLocaleString()}건` : '',
            ].filter(Boolean).join(' · ');
            message.warning({
              content: `${parts}은 보내지 않았습니다 — 목록에서 지금 값을 확인한 뒤 다시 요청해 주세요.`,
              duration: 8,
            });
          }
          if (ids.length === 0) {
            if (changed + gone === 0) message.info('지금 요청할 수 있는 카드가 없습니다 — 목록을 새로 확인해 주세요.');
            return;
          }
          void applyCurrent(ids, p.store);
        }}
      />

      <AppModal
        title={`요청하지 못한 카드 ${failures?.length ?? 0}건`}
        open={!!failures}
        onCancel={() => setFailures(null)}
        footer={<AppButton variant="primary" onClick={() => setFailures(null)}>확인</AppButton>}
      >
        <ul style={{ margin: 0, paddingLeft: SPACING.lg, fontSize: FONT_SIZE.sm, lineHeight: LINE_HEIGHT.relaxed }}>
          {(failures ?? []).map((f) => (
            <li key={f.cardId}>
              <span style={{ fontWeight: FONT_WEIGHT.medium, color: t.textPrimary }}>{f.name}</span>
              <span style={{ color: t.textSecondary }}> — {f.reason}</span>
            </li>
          ))}
        </ul>
      </AppModal>
    </div>
  );
};

/** 재고 칸(감사 card-controltower#348) — 없으면 '-', 0 은 품절로 눈에 띄게. */
const StockCell: React.FC<{ stock: number | null | undefined; t: ThemeTokens }> = ({ stock, t }) => {
  if (stock == null) {
    return <span title="CT 카드 목록에 재고가 없습니다" style={{ color: t.textEmpty }}>-</span>;
  }
  if (stock <= 0) {
    return (
      <span
        data-testid="hub-stock-zero"
        title="재고 0 — 품절"
        style={{
          color: t.danger, background: t.tintDanger, fontWeight: FONT_WEIGHT.bold,
          padding: `0 ${SPACING.xxs}px`, borderRadius: RADIUS.sm, whiteSpace: 'nowrap',
        }}
      >
        0
      </span>
    );
  }
  return <span style={{ color: t.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{stock.toLocaleString('ko-KR')}</span>;
};

/**
 * 매장 가격 요청 줄의 카드 썸네일(감사 card-controltower#439) — 없으면 공용 자리표시, 로드 실패는 깨짐 자리표시. 누르면(또는 Enter)
 * 크게 보인다. 줄 선택 · 펼침과 겹치지 않게 클릭을 위로 올리지 않는다.
 */
const HubCardThumb: React.FC<{ url: string | null | undefined; name: string; width: number; t: ThemeTokens }> = ({ url, name, width, t }) => {
  const height = Math.round(width * 1.4);
  const img = (
    <img
      data-testid="hub-thumb"
      src={url || cardPlaceholder}
      alt={url ? `${name} 이미지` : ''}
      loading="lazy"
      onError={fallbackToBroken}
      style={{
        width, height, objectFit: 'cover', borderRadius: RADIUS.sm, border: `1px solid ${t.border}`,
        background: t.bgPlaceholder, display: 'block', cursor: url ? 'zoom-in' : 'default',
      }}
    />
  );
  if (!url) return img;
  return (
    <Popover
      trigger="click"
      placement="right"
      content={<img src={url} alt={`${name} 크게`} onError={fallbackToBroken} style={{ width: 240, maxHeight: 340, objectFit: 'contain', borderRadius: RADIUS.md, display: 'block' }} />}
    >
      <button
        type="button"
        aria-label={`${name} 이미지 크게 보기`}
        onClick={(e) => e.stopPropagation()}
        style={{
          border: 'none', padding: 0, background: 'transparent', cursor: 'zoom-in',
          // 휴대폰 썸네일(40px)도 누르는 곳은 44px(코덱 리뷰 잡 #318)
          minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {img}
      </button>
    </Popover>
  );
};
